import { useMemo } from 'react'
import { useAdminClaimRequests, useAdminApproveClaimRequest, useAdminDenyClaimRequest } from '../../hooks/useAdmin'
import { DataTable } from '../../components/admin/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import type { AdminClaimRequest } from '../../hooks/useAdmin'

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function ActionCell({ req }: { req: AdminClaimRequest }) {
  const approve = useAdminApproveClaimRequest()
  const deny = useAdminDenyClaimRequest()
  const busy = approve.isPending || deny.isPending
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => approve.mutate(req.id)}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-[12px] font-semibold border-0 cursor-pointer disabled:opacity-50 font-sans"
      >
        Approve
      </button>
      <button
        type="button"
        onClick={() => deny.mutate(req.id)}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg border border-line text-ink text-[12px] font-medium cursor-pointer disabled:opacity-50 font-sans bg-surface"
      >
        Deny
      </button>
    </div>
  )
}

function useColumns(): ColumnDef<AdminClaimRequest, unknown>[] {
  return useMemo(() => [
    {
      id: 'church',
      accessorFn: (r) => r.churchName,
      header: 'Church',
      meta: { label: 'Church' },
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-ink">{row.original.churchName}</div>
          <div className="text-[11px] text-muted mt-0.5 font-mono">{row.original.churchId}</div>
        </div>
      ),
    },
    {
      id: 'claimant',
      accessorFn: (r) => r.userDisplayName,
      header: 'Claimant',
      meta: { label: 'Claimant' },
      cell: ({ row }) => (
        <div>
          <div className="text-ink">{row.original.userDisplayName}</div>
          <div className="text-[12px] text-muted">{row.original.userEmail}</div>
        </div>
      ),
    },
    {
      id: 'contact',
      accessorFn: (r) => r.churchEmail ?? r.churchWebsiteUrl ?? '',
      header: 'Church contact',
      meta: { label: 'Church contact' },
      cell: ({ row }) => {
        const r = row.original
        if (!r.churchEmail && !r.churchWebsiteUrl) return <span className="text-[12px] text-muted">None on file</span>
        return (
          <div className="text-[12px] text-ink-2">
            {r.churchEmail && <div>Email: {r.churchEmail}</div>}
            {r.churchWebsiteUrl && <div>Web: {r.churchWebsiteUrl}</div>}
          </div>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Submitted',
      meta: { label: 'Submitted' },
      cell: ({ getValue }) => <span className="text-muted text-[12px]">{fmt(getValue() as string)}</span>,
      sortingFn: 'datetime',
    },
    {
      id: '_actions',
      header: 'Action',
      enableSorting: false,
      enableColumnFilter: false,
      meta: { skipExport: true },
      cell: ({ row }) => <ActionCell req={row.original} />,
    },
  ], [])
}

export default function AdminClaimRequestsPage() {
  const { data, isLoading } = useAdminClaimRequests()
  const requests = useMemo(() => data?.data ?? [], [data])
  const columns = useColumns()

  return (
    <div>
      <div className="mb-6">
        <h1 className="m-0 text-[24px] font-bold">Claim Requests</h1>
        <p className="text-[13px] text-muted mt-2 m-0 max-w-[560px] leading-[1.5]">
          Churches claimed by users whose email/domain couldn't be automatically verified. Review and approve or deny each request.
        </p>
        {!isLoading && <div className="text-[13px] text-muted mt-1">{requests.length} pending</div>}
      </div>

      <DataTable
        columns={columns}
        data={requests}
        isLoading={isLoading}
        filterable
        emptyMessage="No pending claim requests."
      />
    </div>
  )
}
